/**
 * Site Details Page - Microsoft 365 Style
 * Displays comprehensive site information
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { makeStyles, Button, Text, Badge, Spinner } from '@fluentui/react-components';
import { ArrowLeftRegular, EditRegular, MapRegular, LocationRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';
import SiteGeofenceMap from '../../../components/SiteGeofenceMap';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px',
  },
  header: {
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    minWidth: 'auto',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
  },
  siteHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '20px',
    padding: '24px',
    borderBottom: '1px solid #e1dfdd',
  },
  siteIcon: {
    width: '72px',
    height: '72px',
    borderRadius: '8px',
    backgroundColor: '#0078d4',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    color: 'white',
  },
  siteInfo: {
    flex: 1,
  },
  siteName: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '8px',
  },
  siteSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    marginBottom: '8px',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
    padding: '24px',
  },
  section: {
    marginBottom: '0',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '16px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#605e5c',
    textTransform: 'uppercase',
  },
  fieldValue: {
    fontSize: '14px',
    color: '#323130',
  },
  mapLink: {
    color: '#0078d4',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
});

interface Site {
  id: string;
  name: string;
  site_code?: string;
  gps_code?: string;
  address?: string;
  suburb?: string;
  district?: string;
  region?: string;
  zone_id?: string;
  zone_name?: string;
  site_type_id?: string;
  site_type_name?: string;
  nearest_landmark?: string;
  latitude?: number;
  longitude?: number;
  map_url?: string;
  client_id?: string;
  client_name?: string;
  radius_meters?: number;
  boundary_type?: 'circle' | 'polygon';
  boundary_polygon?: { lat: number; lng: number }[] | null;
}

export const SiteDetailsFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const [site, setSite] = useState<Site | null>(location.state?.site || null);
  const [loading, setLoading] = useState(!location.state?.site);

  const fetchSiteDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-sites');
      if (response.data.status === 'success') {
        const sites = response.data.data || [];
        const foundSite = sites.find((s: Site) => s.id === id);

        if (foundSite) {
          setSite(foundSite);
        } else {
          newToast('error', 'Site not found');
          navigate('/account/sites');
        }
      } else {
        newToast('error', response.data?.message || 'Failed to fetch site details');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    if (!site && id) {
      fetchSiteDetails();
    }
  }, [site, id, fetchSiteDetails]);

  const handleBack = () => {
    navigate('/account/sites');
  };

  const handleEdit = () => {
    if (site) {
      navigate('/account/sites', { state: { editSite: site } });
    }
  };

  const handleSaveBoundary = async (data: {
    boundary_type: 'circle' | 'polygon';
    radius_meters: number;
    boundary_polygon: { lat: number; lng: number }[] | null;
  }) => {
    if (!site) {
      return;
    }

    try {
      const response = await ApiClient.post('/save-site-boundary', {
        site_id: site.id,
        boundary_type: data.boundary_type,
        radius_meters: data.radius_meters,
        boundary_polygon: data.boundary_polygon ? JSON.stringify(data.boundary_polygon) : null,
      });

      if (response.data.status === 'success') {
        newToast('success', 'Geofence boundary saved successfully');
        setSite({
          ...site,
          radius_meters: data.radius_meters,
          boundary_type: data.boundary_type,
          boundary_polygon: data.boundary_polygon,
        });
      } else {
        newToast('error', response.data?.message || 'Failed to save boundary');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Spinner size="large" label="Loading site details..." />
        </div>
      </div>
    );
  }

  if (!site) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Text size={500} weight="semibold">
            Site not found
          </Text>
          <Button appearance="primary" onClick={handleBack} style={{ marginTop: '16px' }}>
            Back to Sites
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Button
            icon={<ArrowLeftRegular />}
            appearance="subtle"
            onClick={handleBack}
            className={styles.backButton}
          />
          <Text className={styles.pageTitle}>Site Details</Text>
        </div>
        <div className={styles.headerActions}>
          <Button icon={<EditRegular />} appearance="primary" onClick={handleEdit}>
            Edit Site
          </Button>
        </div>
      </div>

      {/* Site Card */}
      <ContentCard>
        {/* Site Header */}
        <div className={styles.siteHeader}>
          <div className={styles.siteIcon}>
            <LocationRegular />
          </div>
          <div className={styles.siteInfo}>
            <Text className={styles.siteName}>{site.name}</Text>
            {site.site_code && <Text className={styles.siteSubtitle}>Code: {site.site_code}</Text>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
              {site.zone_name && (
                <Badge appearance="tint" color="brand">
                  {site.zone_name}
                </Badge>
              )}
              {site.site_type_name && (
                <Badge appearance="tint" color="informative">
                  {site.site_type_name}
                </Badge>
              )}
              {site.region && (
                <Badge appearance="outline" color="subtle">
                  {site.region}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Site Details Grid */}
        <div className={styles.contentGrid}>
          {/* Basic Information */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Basic Information</Text>
            <div className={styles.fieldGroup}>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Site Name</Text>
                <Text className={styles.fieldValue}>{site.name}</Text>
              </div>
              {site.site_code && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Site Code</Text>
                  <Text className={styles.fieldValue}>{site.site_code}</Text>
                </div>
              )}
              {site.gps_code && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>GPS Code</Text>
                  <Text className={styles.fieldValue}>{site.gps_code}</Text>
                </div>
              )}
              {site.client_name && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Client</Text>
                  <Text className={styles.fieldValue}>{site.client_name}</Text>
                </div>
              )}
            </div>
          </div>

          {/* Location Information */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Location Information</Text>
            <div className={styles.fieldGroup}>
              {site.address && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Address</Text>
                  <Text className={styles.fieldValue}>{site.address}</Text>
                </div>
              )}
              {site.suburb && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Suburb</Text>
                  <Text className={styles.fieldValue}>{site.suburb}</Text>
                </div>
              )}
              {site.district && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>District</Text>
                  <Text className={styles.fieldValue}>{site.district}</Text>
                </div>
              )}
              {site.region && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Region</Text>
                  <Text className={styles.fieldValue}>{site.region}</Text>
                </div>
              )}
              {site.nearest_landmark && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Nearest Landmark</Text>
                  <Text className={styles.fieldValue}>{site.nearest_landmark}</Text>
                </div>
              )}
            </div>
          </div>

          {/* Classification */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Classification</Text>
            <div className={styles.fieldGroup}>
              {site.zone_name && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Zone</Text>
                  <Text className={styles.fieldValue}>{site.zone_name}</Text>
                </div>
              )}
              {site.site_type_name && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Site Type</Text>
                  <Text className={styles.fieldValue}>{site.site_type_name}</Text>
                </div>
              )}
              {site.map_url && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Map Location</Text>
                  <Button
                    as="a"
                    href={site.map_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    appearance="subtle"
                    icon={<MapRegular />}
                    size="small"
                  >
                    Open in Maps
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Geofence Map */}
        {site && (
          <div style={{ padding: '24px', paddingTop: '0' }}>
            <SiteGeofenceMap
              latitude={site.latitude}
              longitude={site.longitude}
              radiusMeters={site.radius_meters || 100}
              boundaryType={site.boundary_type || 'circle'}
              boundaryPolygon={site.boundary_polygon}
              siteName={site.name}
              onSave={handleSaveBoundary}
            />
          </div>
        )}
      </ContentCard>
    </div>
  );
};

export default SiteDetailsFluentUI;
